import React, { useEffect, useState } from 'react'
import fetchWithAuth from '../../utils/fetchWithAuth'
import AdminRecipeModal from '../../components/admin/AdminRecipeModal'
import MapIngredientModal from '../../components/admin/MapIngredientModal'

type RecipeRef = { id: string; title: string }

type Pending = {
  id: number
  nameCanonical: string
  externalSource: string
  recipeIngredients: Array<{ id: string; recipe: RecipeRef }>
}

type History = {
  id: number
  nameCanonical: string
  externalSource: string
  status: 'VERIFIED' | 'REJECTED' | 'REVIEWED'
  reviewer: string
  reviewedAt: string
  recipeIngredients: Array<{ recipe: RecipeRef }>
}

export default function UnverifiedIngredientsPage() {
  const [tab, setTab] = useState<'pending' | 'history'>('pending')
  const [rows, setRows] = useState<Pending[] | History[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [refresh, setRefresh] = useState(0)
  const [modalRecipe, setModalRecipe] = useState<string | null>(null)
  const [mappingJoinId, setMappingJoinId] = useState<string | null>(null)

  // load on tab or refresh change
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const endpoint =
          tab === 'pending'
            ? '/api/admin/ingredients/unverified'
            : '/api/admin/ingredients/history'
        const raw = await fetchWithAuth(endpoint)
        if (cancelled) return
        const data = (raw as any[]).map(item => ({
          ...item,
          recipeIngredients: item.recipeIngredients ?? [],
        }))
        setRows(data)
      } catch {
        if (!cancelled) {
          setError(
            tab === 'pending'
              ? 'Could not load pending ingredients.'
              : 'Could not load reviewed history.'
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [tab, refresh])

  const doAction = async (
    id: number,
    action: 'verify' | 'review' | 'reject' | 'revert'
  ) => {
    setError('')
    try {
      await fetchWithAuth(`/api/admin/ingredients/${id}/${action}`, {
        method: 'PATCH',
      })
      setRefresh(x => x + 1)
    } catch {
      setError(`Failed to ${action}`)
    }
  }

  return (
    <>
      <h1 className="text-2xl font-semibold mb-4">
        Ingredient Catalog Review
      </h1>

      <div className="mb-4 flex space-x-4">
        <button
          onClick={() => setTab('pending')}
          className={`px-4 py-2 rounded ${
            tab === 'pending'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          Pending
        </button>
        <button
          onClick={() => setTab('history')}
          className={`px-4 py-2 rounded ${
            tab === 'history'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          Reviewed History
        </button>
      </div>

      {loading && <p className="mb-4">Loading…</p>}
      {error && <p className="text-red-600 mb-4">{error}</p>}

      {tab === 'pending' ? (
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border">ID</th>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Source</th>
              <th className="p-2 border">Recipe</th>
              <th className="p-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(rows as Pending[]).map((i, idx) => (
              <tr key={`${i.id}-${idx}`} className="border-t">
                <td className="p-2">{i.id}</td>
                <td className="p-2">{i.nameCanonical}</td>
                <td className="p-2">{i.externalSource}</td>
                <td className="p-2">
                  {i.recipeIngredients.length > 0 ? (
                    <button
                      onClick={() =>
                        setModalRecipe(i.recipeIngredients[0].recipe.id)
                      }
                      className="text-blue-600 hover:underline"
                    >
                      {i.recipeIngredients[0].recipe.title}
                    </button>
                  ) : (
                    <span className="text-gray-500">—</span>
                  )}
                </td>
                <td className="p-2 space-x-2">
                  <button
                    onClick={() => doAction(i.id, 'verify')}
                    className="px-3 py-1 bg-green-600 text-white rounded"
                  >
                    Verify
                  </button>
                  <button
                    onClick={() =>
                      setMappingJoinId(i.recipeIngredients[0].id)
                    }
                    className="px-3 py-1 bg-indigo-600 text-white rounded"
                  >
                    Map
                  </button>
                  <button
                    onClick={() => doAction(i.id, 'reject')}
                    className="px-3 py-1 bg-red-600 text-white rounded"
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border">ID</th>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Source</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Reviewer</th>
              <th className="p-2 border">Reviewed At</th>
              <th className="p-2 border">Recipe</th>
              <th className="p-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(rows as History[]).map((h, idx) => (
              <tr
                key={`${h.id}-${h.reviewedAt}-${idx}`}
                className="border-t"
              >
                <td className="p-2">{h.id}</td>
                <td className="p-2">{h.nameCanonical}</td>
                <td className="p-2">{h.externalSource}</td>
                <td className="p-2">{h.status}</td>
                <td className="p-2">{h.reviewer}</td>
                <td className="p-2">
                  {new Date(h.reviewedAt).toLocaleString()}
                </td>
                <td className="p-2">
                  {h.recipeIngredients.length > 0 ? (
                    <button
                      onClick={() =>
                        setModalRecipe(h.recipeIngredients[0].recipe.id)
                      }
                      className="text-blue-600 hover:underline"
                    >
                      {h.recipeIngredients[0].recipe.title}
                    </button>
                  ) : (
                    <span className="text-gray-500">—</span>
                  )}
                </td>
                <td className="p-2">
                  <button
                    onClick={() => doAction(h.id, 'revert')}
                    className="px-3 py-1 bg-yellow-600 text-white rounded"
                  >
                    Revert
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AdminRecipeModal
        recipeId={modalRecipe}
        isOpen={modalRecipe !== null}
        onClose={() => setModalRecipe(null)}
      />

      <MapIngredientModal
        joinId={mappingJoinId}
        isOpen={mappingJoinId !== null}
        onClose={() => setMappingJoinId(null)}
        onMapped={() => {
          setMappingJoinId(null)
          setRefresh(x => x + 1)
        }}
      />
    </>
  )
}
